import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AxiosError } from 'axios';

import {
  getProfile,
  getPublicPortfolio,
  getReviews,
  getSlots,
  PublicPortfolio,
} from './api';
import { ProfileScene } from './scene';

export default function Profile() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<Kudable.ProfileData | null>(null);
  const [error, setError] = useState<Profile.ProfileError>(null);
  const [reviews, setReviews] = useState<Kudable.Review[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [slotsData, setSlotsData] = useState<Kudable.SlotsData>({
    slots: [],
    slot_duration_minutes: null,
    full_day: false,
  });
  const [bookingOpen, setBookingOpen] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [portfolio, setPortfolio] = useState<PublicPortfolio | null>(null);
  const [sort, setSort] = useState<Profile.ReviewSort>('recent');
  const [badge, setBadge] = useState<Profile.ReviewBadgeFilter>('all');
  const [search, setSearch] = useState('');

  const loadReviews = (
    nextPage: number,
    filters: {
      sort: Profile.ReviewSort;
      badge: Profile.ReviewBadgeFilter;
      q: string;
    },
    replace: boolean,
  ) => {
    setReviewsLoading(true);
    getReviews(token!, nextPage, filters)
      .then((response) => {
        const reviewsPage = response.data;
        setReviews((previousReviews) =>
          replace ? reviewsPage.data : [...previousReviews, ...reviewsPage.data],
        );
        setHasMore(reviewsPage.has_more);
        setPage(nextPage);
      })
      .finally(() => setReviewsLoading(false));
  };

  const loadProfile = useCallback(() => {
    setError(null);
    setData(null);
    getProfile(token!)
      .then((response) => setData(response.data))
      .catch((error: AxiosError) => {
        setError(
          error.response?.status === 404 ? 'not_found' : 'unreachable',
        );
      });
  }, [token]);

  useEffect(() => {
    loadProfile();
    loadReviews(1, { sort: 'recent', badge: 'all', q: '' }, true);

    getPublicPortfolio(token!)
      .then((response) => setPortfolio(response.data))
      .catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleLoadMore = () => {
    loadReviews(page + 1, { sort, badge, q: search }, false);
  };

  const handleSortChange = (next: Profile.ReviewSort) => {
    setSort(next);
    loadReviews(1, { sort: next, badge, q: search }, true);
  };

  const handleBadgeChange = (next: Profile.ReviewBadgeFilter) => {
    setBadge(next);
    loadReviews(1, { sort, badge: next, q: search }, true);
  };

  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleSearchChange = useCallback(
    (query: string) => {
      setSearch(query);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        loadReviews(1, { sort, badge, q: query }, true);
      }, 350);
    },
    [sort, badge, loadReviews],
  );

  const handleBookNow = () => {
    setSlotsLoading(true);
    setBookingOpen(true);
    getSlots(token!)
      .then((response) => setSlotsData(response.data))
      .catch(() => null)
      .finally(() => setSlotsLoading(false));
  };

  return (
    <ProfileScene
      data={data}
      error={error}
      onRetry={loadProfile}
      reviews={reviews}
      hasMore={hasMore}
      reviewsLoading={reviewsLoading}
      onLoadMore={handleLoadMore}
      token={token!}
      slotsData={slotsData}
      slotsLoading={slotsLoading}
      bookingOpen={bookingOpen}
      onBookNow={handleBookNow}
      onBookingClose={() => setBookingOpen(false)}
      portfolio={portfolio}
      sort={sort}
      badge={badge}
      search={search}
      onSortChange={handleSortChange}
      onBadgeChange={handleBadgeChange}
      onSearchChange={handleSearchChange}
    />
  );
}
